import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Form, Table } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import apiClient from '../../../../api/apiClient';
import authServices from '../../../../services/AuthServices';
import { toast } from 'react-toastify';

const OrderHistory = () => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    fromDate: '',
    toDate: '',
    orderNumber: ''
  });

  useEffect(() => {
    // Only call API if orderNumber is empty or has more than 3 characters
    if (filters.orderNumber.length === 0 || filters.orderNumber.length > 3) {
      fetchOrderHistory();
    }
  }, [pagination.page, filters.status, filters.orderNumber]);

  const fetchOrderHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize
      };

      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.orderNumber) params.searchValue = filters.orderNumber;

      const response = await ApiGet('/api/kitchen/orders/history', params);

      if (response.success) {
        const data = response.success?.data?.data || response.success?.data;
        setOrders(data?.records || []);
        setPagination(prev => ({
          ...prev,
          totalRecords: data?.totalRecords || 0,
          totalPages: data?.totalPages || 0
        }));
      } else {
        setError('Failed to load order history');
      }
    } catch (err) {
      setError('Failed to load order history');
      toast.error('Failed to load order history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrderHistory();
  };

  const handleClear = () => {
    setFilters({
      status: '',
      fromDate: '',
      toDate: '',
      orderNumber: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchOrderHistory(), 0);
  };

  const handleExcelExport = async () => {
    setExportLoading(true);
    try {
      const token = authServices.getToken();
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.status) params.append('status', filters.status);

      const response = await apiClient.get(`/api/kitchen/orders/xl_export?${params.toString()}`, {
        headers: { access_token: token },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err) {
      toast.error('Failed to export Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'PENDING': 'warning',
      'CREATED': 'secondary',
      'ACCEPTED_ORDER': 'info',
      'PREPARING_ORDER': 'primary',
      'READY_FOR_ORDER': 'success',
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
          <i className="bi bi-clock-history me-2"></i>
          Order History
        </h3>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Order Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={filters.orderNumber}
                  onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  size="sm"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="ACCEPTED_ORDER">Accepted Order</option>
                  <option value="PREPARING_ORDER">Preparing Order</option>
                  <option value="READY_FOR_ORDER">Ready For Order</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={4} md={8}>
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={handleSearch} style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}>
                  <i className="bi bi-search me-1"></i> Search
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleClear}>
                  <i className="bi bi-x-circle me-1"></i> Clear
                </Button>
                <Button variant="success" size="sm" onClick={handleExcelExport} disabled={exportLoading}>
                  {exportLoading ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Exporting...</>
                  ) : (
                    <><i className="bi bi-file-earmark-excel me-1"></i> Excel</>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Orders Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Loading orders...</div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox fs-1 text-muted d-block mb-3" style={{ opacity: 0.5 }}></i>
              <h5 className="text-muted">No Orders Found</h5>
              <p className="text-muted mb-0">No orders match your search criteria.</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr style={{ backgroundColor: '#3b82f6' }}>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order Id</th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order Type</th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Table Number</th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order Items </th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Status</th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Created At</th>
                    <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', fontSize: '14px' }}>
                      <td className="fw-bold align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{order.orderNumber}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Badge bg={order.orderType === 'DINE_IN' ? 'primary' : order.orderType === 'ONLINE' ? 'success' : 'info'}>
                          {order.orderType?.replace('_', ' ') || 'N/A'}
                        </Badge>
                      </td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{order.tableNumber || '-'}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <details style={{ cursor: 'pointer' }}>
                          <summary style={{ listStyle: 'none' }}>
                            <span style={{
                              color: '#3b82f6',
                              display: 'inline-flex',
                              alignItems: 'center',
                              textDecoration: 'underline'
                            }}>
                              <i className="bi bi-eye me-1"></i>
                              View Details ({order.orderItems?.length || 0} items)
                            </span>
                          </summary>
                          <div style={{ marginTop: '8px', paddingLeft: '10px', borderLeft: '2px solid #e2e8f0', textAlign: 'left' }}>
                            {(order.orderItems || []).map((item, idx) => (
                              <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: idx < order.orderItems.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                <div className="fw-bold">{item.menuItemName || item.name || 'Item'}</div>
                                <div style={{ color: '#6b7280' }}>Qty: {item.quantity}</div>
                                {item.specialInstructions && (
                                  <div style={{ color: '#f59e0b', marginTop: '4px' }}>
                                    <i className="bi bi-info-circle me-1"></i>
                                    {item.specialInstructions}
                                  </div>
                                )}
                                {item.addonItems && item.addonItems.length > 0 && (
                                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                                    <strong>Addons:</strong>
                                    {item.addonItems.map((addon, addonIdx) => (
                                      <div key={addonIdx} style={{ paddingLeft: '8px' }}>
                                        + {addon.name} (x{addon.quantity})
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{getStatusBadge(order.status)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{formatDateTime(order.createdAt)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div className="fw-bold">{order.customerName || '-'}</div>
                        {order.customerPhone && (
                          <div style={{ color: '#6b7280', marginTop: '2px' }}>
                            <i className="bi bi-telephone me-1"></i>
                            {order.customerPhone}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <small className="text-muted">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.totalRecords)} of {pagination.totalRecords} orders
                </small>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <i className="bi bi-chevron-left"></i> Previous
                  </Button>
                  <span className="align-self-center px-2">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default OrderHistory;
